// Web Audio API-based reminder tones.
// No bundled audio files required — everything is synthesized on the fly.
// Each preset returns a play() function that resolves when done.

const SR = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  // Reuse a single context to avoid throwing after many plays.
  if (!SR._ctx) SR._ctx = new Ctx();
  // Some browsers suspend the context until a user gesture.
  if (SR._ctx.state === "suspended" && SR._ctx.resume) {
    try { SR._ctx.resume(); } catch (_) { /* noop */ }
  }
  return SR._ctx;
};

function playSequence(notes, { volume = 0.18 } = {}) {
  const ctx = SR();
  if (!ctx) return Promise.resolve();
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = volume;
  master.connect(ctx.destination);
  let cursor = now;
  let lastEnd = now;
  notes.forEach(({ freq, duration = 0.2, wave = "sine", startOffset = 0 }) => {
    const start = cursor + startOffset;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = wave;
    osc.frequency.setValueAtTime(freq, start);
    // ADSR envelope for a pleasant chime
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(1, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain).connect(master);
    osc.start(start);
    osc.stop(start + duration + 0.02);
    cursor = start + duration * 0.6; // slight overlap
    if (start + duration > lastEnd) lastEnd = start + duration;
  });
  const totalMs = Math.max(0, (lastEnd - now) * 1000 + 60);
  return new Promise((resolve) => setTimeout(resolve, totalMs));
}

// Track any running loops so we can stop them from outside.
const _activeLoops = new Map(); // id -> { stop: () => void, id }

/**
 * Play a reminder alarm — loops the chosen preset with 0.5s gaps until:
 *   - the caller invokes stopAlarm(handle), OR
 *   - it reaches maxDurationMs (default 60s hard cap so we never spin forever)
 * Returns a handle {stop, id}.
 */
export function startAlarm(soundId, { maxDurationMs = 60_000 } = {}) {
  const preset = SOUND_PRESETS[soundId] || SOUND_PRESETS.chime;
  if (soundId === "none") return { stop: () => {}, id: null };
  let cancelled = false;
  let cancelTimer = null;
  const id = `alarm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const loop = async () => {
    while (!cancelled) {
      try { await preset.play(); } catch { /* noop */ }
      if (cancelled) return;
      await new Promise((r) => setTimeout(r, 500));
    }
  };
  loop();
  cancelTimer = setTimeout(() => { cancelled = true; _activeLoops.delete(id); }, maxDurationMs);
  const handle = {
    id,
    stop: () => {
      cancelled = true;
      if (cancelTimer) clearTimeout(cancelTimer);
      _activeLoops.delete(id);
    },
  };
  _activeLoops.set(id, handle);
  return handle;
}

export function stopAllAlarms() {
  for (const h of _activeLoops.values()) { try { h.stop(); } catch { /* noop */ } }
  _activeLoops.clear();
}

export const SOUND_PRESETS = {
  gentle: {
    id: "gentle",
    label: "Gentle",
    description: "Soft two-note bell",
    play: () =>
      playSequence(
        [
          { freq: 660, duration: 0.55, wave: "sine" },
          { freq: 880, duration: 0.85, wave: "sine", startOffset: 0.05 },
        ],
        { volume: 0.15 },
      ),
  },
  chime: {
    id: "chime",
    label: "Chime",
    description: "Bright three-note chime",
    play: () =>
      playSequence(
        [
          { freq: 784, duration: 0.28, wave: "triangle" },
          { freq: 988, duration: 0.28, wave: "triangle" },
          { freq: 1319, duration: 0.5, wave: "triangle" },
        ],
        { volume: 0.18 },
      ),
  },
  alert: {
    id: "alert",
    label: "Alert",
    description: "Assertive double beep",
    play: () =>
      playSequence(
        [
          { freq: 1046, duration: 0.18, wave: "square" },
          { freq: 1046, duration: 0.18, wave: "square", startOffset: 0.14 },
        ],
        { volume: 0.14 },
      ),
  },
  none: {
    id: "none",
    label: "Silent",
    description: "No sound",
    play: () => Promise.resolve(),
  },
};

export const SOUND_OPTIONS = ["gentle", "chime", "alert", "none"];

export function playSound(id) {
  const preset = SOUND_PRESETS[id] || SOUND_PRESETS.chime;
  try {
    return preset.play();
  } catch (_) {
    return Promise.resolve();
  }
}

export function getSoundLabel(id) {
  return (SOUND_PRESETS[id] || SOUND_PRESETS.chime).label;
}
